import { Outlet, useLocation } from "react-router";
import styles from "./index.module.scss";
import { Menu } from "./menu";
import ChatContext from "@medoma/chat/ChatContext";
import { applicationSchema, useMatrixClient } from "@medoma/chat/matrixClient";
import { useEffect } from "react";
import { getCareJwt } from "./api/chat";
import { ClientEvent } from "matrix-js-sdk";
import clsx from "clsx";
import { notificationApi } from "./api/apiClient";
import { usePatient } from "@/api/patient";
import { UnreadMessageBannerContent } from "@/components/UnreadMessageBannerContent";
import { useHasUnreadNotifications } from "@medoma/chat/useUnreadNotificationCounts";
import { Banner } from "./components/Banner";
import { useUnreadNotifications } from "@medoma/chat/useUnreadNotifications";
import { useCareSSEHandlers } from "./utils/sseHandlers";
import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { callIsOngoingQueryOptions } from "./api/callStatus";
import { OngoingVideoCallBannerContent } from "./components/OngoingVideoCallBannerContent";

const ChatProvider = ({ children }: { children: React.ReactNode }) => {
  const matrixClient = useMatrixClient(applicationSchema.enum.Care, getCareJwt);

  // Use debug chat
  useEffect(() => {
    if (matrixClient === null) {
      return;
    }

    if (import.meta.env.VITE_DEBUG_CHAT) {
      matrixClient.on(ClientEvent.Event, console.log);

      return () => {
        matrixClient.off(ClientEvent.Event, console.log);
      };
    }
  }, [matrixClient]);

  const unreadNotifications = useUnreadNotifications(matrixClient);

  return (
    <ChatContext.Provider value={{ matrixClient, unreadNotifications }}>
      {children}
    </ChatContext.Provider>
  );
};

const useRegisterForPushNotifications = () => {
  useEffect(() => {
    // @ts-expect-error PUSHY_TOKEN is injected on window object from native app
    const pushyToken: string | undefined = window.PUSHY_TOKEN;
    // @ts-expect-error DEVICE_NAME is injected on window object from native app
    const deviceName: string | undefined = window.DEVICE_NAME;
    if (!pushyToken) {
      console.warn("No Pushy token found, skipping registration.");
    } else {
      notificationApi
        .put(`/device`, {
          json: { deviceName, registrationToken: pushyToken },
        })
        .catch((error: unknown) => Sentry.captureException(error));
    }
  }, []);
};

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}
const useNotifyWebAppIsReady = () => {
  useEffect(() => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage("app:ready");
    }
  }, []);
};

export const Banners = ({
  hasUnreadNotification,
}: {
  hasUnreadNotification: boolean;
}) => {
  const { data: callIsOngoing } = useQuery(callIsOngoingQueryOptions);
  const location = useLocation();
  const isInChat = location.pathname.endsWith("chat");
  const isInVideoCall = location.pathname.endsWith("video");

  const showVideoCallBanner = !isInVideoCall && callIsOngoing === true;
  const showChatBanner =
    !showVideoCallBanner && !isInChat && hasUnreadNotification;
  return (
    <>
      <Banner isVisible={showVideoCallBanner} linkTo="/video">
        <OngoingVideoCallBannerContent />
      </Banner>
      <Banner isVisible={showChatBanner} linkTo="/chat">
        <UnreadMessageBannerContent
          hasUnreadNotification={hasUnreadNotification}
        />
      </Banner>
    </>
  );
};

const InnerComponent = () => {
  const { data: patient } = usePatient();

  useEffect(() => {
    if (patient) {
      Sentry.setUser({
        username: `${patient.id}`,
      });
    }
  }, [patient]);

  const hasUnreadNotification = useHasUnreadNotifications({
    roomName: patient?.id,
  });

  return (
    <>
      <div className={styles.leftPanel}>
        <Banners hasUnreadNotification={hasUnreadNotification} />
        <main className={styles.main}>
          <Outlet />
        </main>
      </div>
      <Menu hasUnreadNotification={hasUnreadNotification} />
    </>
  );
};

export const Index = () => {
  useNotifyWebAppIsReady();
  useRegisterForPushNotifications();
  useCareSSEHandlers();

  return (
    <div className={clsx([styles.app, styles.componentVariableOverrides])}>
      <ChatProvider>
        <InnerComponent />
      </ChatProvider>
    </div>
  );
};
